<template>
  <div
    ref="datepickerWrapperRef"
    v-click-outside="() => (showPicker = false)"
    class="relative flex flex-col gap-2"
    :class="`daterange-wrapper-${UID}`"
    :data-testid="dataTestid"
  >
    <div
      v-if="label"
      class="flex gap-1 items-center"
      :class="labelClass"
    >
      <strong>{{ label }}</strong>
    </div>

    <div
      class="relative min-h-12"
      @mouseover="flags.hover = 1"
      @mouseleave="flags.hover = 0"
    >
      <div
        class="absolute z-[2] top-[0.875rem] right-[0.6875rem] w-5 h-5 flex place-items-center justify-center"
        :class="{
          'bg-semantic-surface-surface': status === 'default' && !disabled,
        }"
      >
        <V2IconsCalendar
          :state="getCalendarIconState(status, errorMessage)"
          width="18"
          height="20"
          :class="{
            'cursor-not-allowed': disabled,
            'cursor-pointer': !disabled,
          }"
          @click="openPicker"
        />
      </div>

      <V2AtomsInput
        v-model="rangeText"
        type="text"
        model-type="text"
        :placeholder="placeholder || `${format} to ${format}`"
        :error-message="errorMessage"
        :is-success="startIsSuccess && endIsSuccess"
        :disabled="disabled"
        :be-active="showPicker"
        :mobile-keyboard-type="mobileKeyboardType"
        hide-icon
        maxlength="40"
        :data-testid="`${dataTestid}-input`"
        @focus="() => {
          flags.focus = 1
          openPicker()
        }"
        @blur="flags.focus = 0"
        @click="openPicker"
      />
    </div>

    <ClientOnly>
      <date-picker
        ref="datepicker"
        v-model:value="rangeValue"
        v-model:open="showPicker"
        range
        type="date"
        :class="`daterange-anchor datepicker-${UID}`"
        :value-type="valueType"
        :format="format"
        :disabled="disabled"
        :disabled-date="disableDate"
        :popup-class="`datepicker-popup-${UID} daterange-popup`"
      />
    </ClientOnly>
  </div>
</template>

<script setup>
import 'vue-datepicker-next/index.css'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'

dayjs.extend(customParseFormat)

defineOptions({
  name: 'V2AtomsDaterangePicker'
})

const props = defineProps({
  format: {
    type: String,
    default: 'DD/MM/YYYY'
  },

  valueType: {
    type: String,
    default: 'DD/MM/YYYY'
  },

  label: {
    type: String,
    default: ''
  },

  placeholder: {
    type: String,
    default: ''
  },

  startErrorMessage: {
    type: String,
    default: ''
  },

  endErrorMessage: {
    type: String,
    default: ''
  },

  startIsSuccess: {
    type: Boolean,
    default: false
  },

  endIsSuccess: {
    type: Boolean,
    default: false
  },

  // This will disable the date selection in date picker
  disableDate: {
    type: Function,
    default: () => false
  },

  disabled: {
    type: Boolean,
    default: false
  },

  mobileKeyboardType: {
    type: String,
    default: 'text'
  },

  labelClass: {
    type: String,
    default: ''
  },

  dataTestid: {
    type: String,
    default: ''
  }
})

const emit = defineEmits([
  'input'
])

const UID = useId()

const datepickerWrapperRef = ref()
const datepicker = ref(null)

const startDate = defineModel('startDate', { type: String, default: null })
const endDate = defineModel('endDate', { type: String, default: null })

const showPicker = ref(false)

const flags = reactive({
  focus: 0,
  hover: 0
})

/**
 * vue-datepicker-next range mode works with a `[start, end]` pair.
 * It only sets the pair once both dates are picked, and swaps them
 * when the second pick is earlier than the first.
 */
const rangeValue = computed({
  get: () => [startDate.value || null, endDate.value || null],
  set: value => {
    const [start, end] = value || []

    startDate.value = start || null
    endDate.value = end || null
  }
})

/**
 * One text field holding "DD/MM/YYYY to DD/MM/YYYY (x days)". Typed digits
 * are masked into that shape and parsed back into the start/end date
 * models; picking from the calendar rewrites the text. The "(x days)"
 * suffix is decoration only — it is stripped before masking/parsing and
 * re-appended once both dates are complete.
 */
const rangeText = ref(composeRange(startDate.value, endDate.value))

const errorMessage = computed(() => props.startErrorMessage || props.endErrorMessage)

const status = computed(() => {
  if (props.disabled) {
    return 'disabled'
  }

  if (flags.hover) {
    return 'hovered'
  }

  if (flags.focus) {
    return 'focused'
  }

  if (errorMessage.value) {
    return 'error'
  }

  if ((props.startIsSuccess && props.endIsSuccess) || startDate.value || endDate.value) {
    return 'success'
  }

  return 'default'
})

const datepickerPopupEl = computed(() => (import.meta && document.querySelector(`.datepicker-popup-${UID}`)) || null)

// Marks values written back by the rangeText watcher itself, so the
// re-fire isn't reprocessed as user input (a shortened rewrite would
// otherwise be mistaken for a deletion)
let internalRangeText = null

watch(rangeText, (value, oldValue) => {
  if (value === internalRangeText) {
    internalRangeText = null
    return
  }

  internalRangeText = null

  const isDeleting = (value?.length ?? 0) < (oldValue?.length ?? 0)
  let core = stripDaysSuffix(value)

  // Backspacing into the "(x days)" suffix should start eating the end date
  if (isDeleting && core === stripDaysSuffix(oldValue)) {
    core = core.slice(0, -1)
  }

  const masked = isDeleting ? core : maskRange(core)
  const [start, end] = parseRange(masked)
  const next = start && end ? composeRange(start, end) : masked

  if (next !== value) {
    internalRangeText = next
    rangeText.value = next
  }

  if (start !== startDate.value) {
    startDate.value = start
  }

  if (end !== endDate.value) {
    endDate.value = end
  }
})

watch([startDate, endDate], ([start, end]) => {
  const [typedStart, typedEnd] = parseRange(rangeText.value)

  if (typedStart !== (start || null) || typedEnd !== (end || null)) {
    rangeText.value = composeRange(start, end)
  }
})

watch(startDate, value => {
  emit('input', { field: 'startDate', value })
})

watch(endDate, value => {
  emit('input', { field: 'endDate', value })
})

function composeRange (start, end) {
  if (start && end) {
    const days = countDays(start, end)

    return `${start} to ${end}${days ? ` (${days} ${days === 1 ? 'day' : 'days'})` : ''}`
  }

  return start || ''
}

// The date part never contains "(", so anything from "(" on is the
// (possibly partially deleted) days suffix
function stripDaysSuffix (text) {
  return `${text ?? ''}`.replace(/\s*\(.*$/, '')
}

// Inclusive day count: start and end dates both count as travel days
function countDays (start, end) {
  const days = dayjs(end, 'DD/MM/YYYY').diff(dayjs(start, 'DD/MM/YYYY'), 'day') + 1

  return days > 0 ? days : null
}

function parseRange (text) {
  const match = `${text ?? ''}`.match(/^(\d{2}\/\d{2}\/\d{4})(?: to (\d{2}\/\d{2}\/\d{4}))?/)

  return [match?.[1] ?? null, match?.[2] ?? null]
}

/**
 * Reflows raw typed input into "DD/MM/YYYY to DD/MM/YYYY",
 * clamping day/month parts the same way the single Datepicker does
 */
function maskRange (raw) {
  let digits = `${raw ?? ''}`.replace(/\D/g, '').slice(0, 16)

  const spec = [
    { len: 2, max: 31, sep: '/' },
    { len: 2, max: 12, sep: '/' },
    { len: 4, sep: ' to ' },
    { len: 2, max: 31, sep: '/' },
    { len: 2, max: 12, sep: '/' },
    { len: 4, sep: '' }
  ]

  let out = ''

  for (const { len, max, sep } of spec) {
    if (!digits.length) {
      break
    }

    let part = digits.slice(0, len)
    digits = digits.slice(len)

    // 0-pad a lone digit that can't start a valid value (e.g. day "4" -> "04")
    if (max && part.length === 1 && !digits.length && Number(part) > Number(`${max}`[0])) {
      part = `0${part}`
    }

    if (max && part.length === len) {
      const num = parseInt(part, 10)

      if (num < 1) {
        part = '01'
      } else if (num > max) {
        part = `${max}`
      }
    }

    out += part

    if (part.length === len) {
      out += sep
    }
  }

  return out
}

function getCalendarIconState (currStatus, currErrorMessage) {
  if (currStatus === 'error' || currErrorMessage) {
    return 'error'
  }

  if (['hovered', 'focused'].includes(currStatus) || showPicker.value) {
    return 'active'
  }

  return 'default'
}

function openPicker () {
  if (props.disabled) {
    return
  }

  showPicker.value = true
}

defineExpose({
  status,
  startDate,
  endDate,
  rangeText,
  showPicker,
  $el: datepickerWrapperRef,
  datepicker,
  UID,
  datepickerPopupClass: `datepicker-popup-${UID}`,
  datepickerPopupEl
})
</script>
